import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { renderUserAvatar } from "@/lib/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Inbox } from "lucide-react";
import { useDrawer } from "@/components/RightDrawer";
import { toast } from "sonner";

const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const PRIORITY_COLORS = {
  Low: "bg-zinc-100 text-zinc-700",
  Medium: "bg-blue-100 text-blue-700",
  High: "bg-orange-100 text-orange-700",
  Urgent: "bg-red-100 text-red-700",
};

const REQUEST_TYPES = [
  { id: "general", label: "General" },
  { id: "escalation", label: "Escalation" },
  { id: "access", label: "Access" },
  { id: "procurement", label: "Procurement" },
  { id: "it_support", label: "IT support" },
  { id: "hr", label: "HR" },
  { id: "other", label: "Other" },
];

const DEFAULT_STATUSES = [
  "Submitted", "In Review", "In Progress", "Waiting for Information",
  "Approved", "Rejected", "Completed", "Cancelled",
];

const OPEN_STATUSES = new Set(["Submitted", "In Review", "In Progress", "Waiting for Information"]);
const DONE_STATUSES = new Set(["Approved", "Rejected", "Completed", "Cancelled"]);

export default function RequestsView({ subscribe }) {
  const { openDrawer } = useDrawer();
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
  const [tab, setTab] = useState("open");

  const load = async () => {
    const [r, m] = await Promise.all([api.get("/requests"), api.get("/orgs/current/members")]);
    setItems(r.data);
    setMembers(m.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/requests-meta/statuses").then((r) => setStatuses(r.data.statuses)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((type) => { if (type.startsWith("request.")) load(); });
  }, [subscribe]);

  const groups = useMemo(() => ({
    open: items.filter((r) => OPEN_STATUSES.has(r.status)),
    closed: items.filter((r) => DONE_STATUSES.has(r.status)),
  }), [items]);

  const openDetail = (r) => openDrawer("Request", r.id, <RequestDetail id={r.id} members={members} statuses={statuses} onChange={load}/>);
  const visible = tab === "open" ? groups.open : groups.closed;

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white" data-testid="requests-view">
      <div className="h-14 border-b flex items-center justify-between px-6 shrink-0">
        <h1 className="text-lg font-semibold flex items-center gap-2"><Inbox className="w-5 h-5 text-[var(--brand-accent)]"/>Requests</h1>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border overflow-hidden text-sm">
            <button onClick={() => setTab("open")} data-testid="req-tab-open"
              className={`px-3 py-1.5 ${tab === "open" ? "bg-[var(--brand-primary)] text-white" : "bg-white"}`}>Open · {groups.open.length}</button>
            <button onClick={() => setTab("closed")} data-testid="req-tab-closed"
              className={`px-3 py-1.5 ${tab === "closed" ? "bg-[var(--brand-primary)] text-white" : "bg-white"}`}>Closed · {groups.closed.length}</button>
          </div>
          <NewRequestDialog members={members} statuses={statuses} onCreated={load}/>
        </div>
      </div>
      <div className="flex-1 overflow-auto scroll-thin p-6">
        {visible.length === 0 ? (
          <div className="text-sm text-zinc-500 border rounded-lg p-8 text-center">
            No {tab} requests. {tab === "open" && "Submit one to route work to the right owner."}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {visible.map((r) => <RequestCard key={r.id} r={r} onClick={openDetail}/>)}
          </div>
        )}
      </div>
    </div>
  );
}

function RequestCard({ r, onClick }) {
  const typeLabel = REQUEST_TYPES.find((t) => t.id === r.request_type)?.label || r.request_type;
  return (
    <div onClick={() => onClick(r)} className="p-4 border rounded-lg cursor-pointer hover:bg-zinc-50 bg-white" data-testid={`request-card-${r.id}`}>
      <div className="flex items-center justify-between mb-1">
        <span className={`text-[10px] rounded-full px-2 py-0.5 ${PRIORITY_COLORS[r.priority] || "bg-zinc-100 text-zinc-700"}`}>{r.priority}</span>
        <span className="text-xs text-zinc-500">{r.status}</span>
      </div>
      <div className="font-medium truncate">{r.title}</div>
      <div className="text-xs text-zinc-500 mt-1 flex items-center gap-2">
        <span>{typeLabel}</span>
        {r.department && <><span className="text-zinc-300">·</span><span>{r.department}</span></>}
      </div>
      <div className="mt-2 flex items-center justify-between text-xs">
        <div className="flex items-center gap-1 text-zinc-500">
          {r.requester && <>
            <img src={renderUserAvatar(r.requester)} className="w-4 h-4 rounded-full object-cover" alt=""/>
            <span>{r.requester.name}</span>
          </>}
        </div>
        {r.owner && (
          <div className="flex items-center gap-1 text-zinc-500">
            <span>→</span>
            <img src={renderUserAvatar(r.owner)} className="w-4 h-4 rounded-full object-cover" alt=""/>
            <span>{r.owner.name}</span>
          </div>
        )}
      </div>
      {r.due_date && <div className="text-[11px] text-zinc-400 mt-1 mono">Due {r.due_date}</div>}
    </div>
  );
}

function NewRequestDialog({ members, statuses, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", request_type: "general", description: "", priority: "Medium", status: "Submitted", owner_id: "", department: "", due_date: "" });
  const reset = () => setF({ title: "", request_type: "general", description: "", priority: "Medium", status: "Submitted", owner_id: "", department: "", due_date: "" });
  const create = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    try {
      await api.post("/requests", f);
      setOpen(false); reset();
      toast.success("Request submitted");
      onCreated();
    } catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-[var(--brand-accent)] text-white" data-testid="new-request-btn"><Plus className="w-4 h-4 mr-1"/> New Request</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Submit a request</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="request-title"/>
          <div className="grid grid-cols-2 gap-2">
            <Select value={f.request_type} onValueChange={(v) => setF({ ...f, request_type: v })}>
              <SelectTrigger data-testid="request-type"><SelectValue/></SelectTrigger>
              <SelectContent>{REQUEST_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
              <SelectTrigger data-testid="request-priority"><SelectValue/></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Textarea placeholder="Describe what you need…" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="request-description"/>
          <div className="grid grid-cols-2 gap-2">
            <Select value={f.owner_id || undefined} onValueChange={(v) => setF({ ...f, owner_id: v })}>
              <SelectTrigger data-testid="request-owner"><SelectValue placeholder="Assign to (optional)"/></SelectTrigger>
              <SelectContent>{members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Department (optional)" value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })}/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })}/>
          </div>
          <Button className="w-full bg-[var(--brand-primary)] text-white" onClick={create} data-testid="request-create-submit">Submit request</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RequestDetail({ id, members, statuses, onChange }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  useEffect(() => { api.get(`/requests/${id}`).then((r) => setData(r.data)); }, [id]);
  const patch = async (p) => {
    await api.patch(`/requests/${id}`, p);
    const r = await api.get(`/requests/${id}`);
    setData(r.data);
    onChange && onChange();
  };
  const addComment = async () => {
    if (!text.trim()) return;
    await api.post(`/requests/${id}/comments`, { text });
    const r = await api.get(`/requests/${id}`);
    setData(r.data);
    setText("");
  };
  if (!data) return <div className="p-4 text-sm text-zinc-500">Loading…</div>;
  const r = data.request;
  return (
    <div className="p-4 space-y-4">
      <Input value={r.title} onChange={(ev) => setData({ ...data, request: { ...r, title: ev.target.value } })}
        onBlur={(ev) => patch({ title: ev.target.value })} className="text-lg font-semibold border-0 px-0" data-testid="request-detail-title"/>
      <Textarea value={r.description || ""} onChange={(ev) => setData({ ...data, request: { ...r, description: ev.target.value } })}
        onBlur={(ev) => patch({ description: ev.target.value })} placeholder="Description"/>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs uppercase text-zinc-500">Type</label>
          <Select value={r.request_type} onValueChange={(v) => patch({ request_type: v })}>
            <SelectTrigger><SelectValue/></SelectTrigger>
            <SelectContent>{REQUEST_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase text-zinc-500">Priority</label>
          <Select value={r.priority} onValueChange={(v) => patch({ priority: v })}>
            <SelectTrigger><SelectValue/></SelectTrigger>
            <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs uppercase text-zinc-500">Status</label>
          <Select value={r.status} onValueChange={(v) => patch({ status: v })}>
            <SelectTrigger data-testid="request-detail-status"><SelectValue/></SelectTrigger>
            <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase text-zinc-500">Due</label>
          <Input type="date" defaultValue={r.due_date || ""} onBlur={(e) => patch({ due_date: e.target.value || null })}/>
        </div>
      </div>
      <div>
        <label className="text-xs uppercase text-zinc-500">Assigned to</label>
        <Select value={r.owner_id || undefined} onValueChange={(v) => patch({ owner_id: v })}>
          <SelectTrigger><SelectValue placeholder="Unassigned"/></SelectTrigger>
          <SelectContent>{members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs uppercase text-zinc-500">Department</label>
        <Input defaultValue={r.department || ""} onBlur={(e) => patch({ department: e.target.value })}/>
      </div>
      <div>
        <label className="text-xs uppercase text-zinc-500">Resolution</label>
        <Textarea defaultValue={r.resolution || ""} onBlur={(e) => patch({ resolution: e.target.value })} placeholder="Add resolution notes when closing"/>
      </div>
      <div>
        <div className="text-xs uppercase text-zinc-500 mb-2">Comments</div>
        {(data.comments || []).map((c) => (
          <div key={c.id} className="flex gap-2 mb-2">
            {c.user && <img src={renderUserAvatar(c.user)} className="w-6 h-6 rounded-full object-cover" alt=""/>}
            <div>
              <div className="text-xs text-zinc-500">{c.user?.name}</div>
              <div className="text-sm">{c.text}</div>
            </div>
          </div>
        ))}
        <div className="flex gap-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add comment…" data-testid="request-comment-input"/>
          <Button onClick={addComment} size="sm" data-testid="request-comment-send">Send</Button>
        </div>
      </div>
      <div>
        <div className="text-xs uppercase text-zinc-500 mb-2">Activity</div>
        <div className="space-y-1 text-xs text-zinc-600 max-h-56 overflow-y-auto scroll-thin">
          {(data.activity || []).map((a) => (
            <div key={a.id}><b>{a.actor?.name}</b> <span className="text-zinc-400">{a.action}</span> · {new Date(a.created_at).toLocaleString()}</div>
          ))}
        </div>
      </div>
    </div>
  );
}
